import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  DatePicker,
  Button,
  Space,
  Spin,
  Statistic,
  List,
  Table,
  Typography,
  Divider,
} from 'antd';
import {
  ReloadOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  HourglassOutlined,
  FundOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { fetchProductionFinances } from '../utils/fetch';
import { getDepartmentName } from '../utils/departmentDetector';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const ProductionFinances = ({ departmentType }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState([
    moment().startOf('month'),
    moment().endOf('day'),
  ]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const startDate = dateRange?.[0]?.format('YYYY-MM-DD');
  const endDate = dateRange?.[1]?.format('YYYY-MM-DD');

  const formatCurrency = useCallback((value = 0) => {
    return new Intl.NumberFormat('kk-KZ', {
      style: 'currency',
      currency: 'KZT',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number.isFinite(value) ? value : 0);
  }, []);

  const formatPercent = useCallback((value = 0) => {
    const formatted = Number.isFinite(value) ? value : 0;
    return `${formatted.toFixed(2)}%`;
  }, []);

  const setQuickRange = (type) => {
    switch (type) {
      case 'today':
        setDateRange([moment().startOf('day'), moment().endOf('day')]);
        break;
      case 'week':
        setDateRange([moment().subtract(6, 'days').startOf('day'), moment().endOf('day')]);
        break;
      case 'month':
        setDateRange([moment().startOf('month'), moment().endOf('day')]);
        break;
      default:
        break;
    }
  };

  const loadData = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const response = await fetchProductionFinances(
        startDate,
        endDate,
        departmentType,
        page,
        pageSize
      );
      setData(response);
    } catch (error) {
      console.error('Ошибка загрузки финансов отдела:', error);
    } finally {
      setLoading(false);
    }
  }, [departmentType, endDate, startDate, page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Сбросить страницу при изменении диапазона дат
  useEffect(() => {
    setPage(1);
  }, [startDate, endDate]);

  const summaryCards = useMemo(() => {
    if (!data?.summary) {
      return [];
    }
    const { summary } = data;
    return [
      {
        key: 'orders',
        title: 'Всего заказов',
        value: summary.totalOrders,
        icon: <TeamOutlined />,
        formatter: (value) => value,
      },
      {
        key: 'inProgress',
        title: 'В процессе',
        value: summary.inProgressCount,
        icon: <HourglassOutlined />,
        formatter: (value) => value,
      },
      {
        key: 'completed',
        title: 'Завершено',
        value: summary.completedCount,
        icon: <CheckCircleOutlined />,
        formatter: (value) => value,
      },
      {
        key: 'totalAmount',
        title: 'Сумма договоров',
        value: summary.totalAmount,
        icon: <FundOutlined />,
        formatter: formatCurrency,
      },
      {
        key: 'advance',
        title: 'Получен аванс',
        value: summary.advanceAmount,
        icon: <FundOutlined />,
        formatter: formatCurrency,
      },
      {
        key: 'balance',
        title: 'Остаток к оплате',
        value: summary.balanceAmount,
        icon: <FundOutlined />,
        formatter: formatCurrency,
      },
      {
        key: 'avg',
        title: 'Средний чек',
        value: summary.averageCheck,
        icon: <FundOutlined />,
        formatter: formatCurrency,
      },
    ];
  }, [data?.summary, formatCurrency]);

  const columns = useMemo(
    () => [
      {
        title: 'Дата',
        dataIndex: 'date',
        key: 'date',
        width: 110,
        fixed: 'left',
      },
      {
        title: 'Клиент',
        dataIndex: 'client',
        key: 'client',
        width: 200,
        render: (text, record) => {
          // Если есть clientId, делаем ссылку
          if (record.clientId) {
            return (
              <Typography.Link
                onClick={() => navigate(`/client/${record.clientId}`)}
                style={{ cursor: 'pointer' }}
              >
                {text}
              </Typography.Link>
            );
          }
          return text;
        },
      },
      {
        title: 'Спецификация',
        dataIndex: 'specification',
        key: 'specification',
        width: 220,
      },
      {
        title: 'квД',
        dataIndex: 'quantity',
        key: 'quantity',
        width: 80,
        align: 'center',
      },
      {
        title: 'Сумма',
        dataIndex: 'totalAmount',
        key: 'totalAmount',
        width: 140,
        render: (value) => formatCurrency(value),
      },
      {
        title: 'Аванс',
        dataIndex: 'advance',
        key: 'advance',
        width: 140,
        render: (value) => formatCurrency(value),
      },
      {
        title: 'Остаток',
        dataIndex: 'balance',
        key: 'balance',
        width: 140,
        render: (value) => formatCurrency(value),
      },
      {
        title: '№ договора',
        dataIndex: 'contractNumber',
        key: 'contractNumber',
        width: 140,
        render: (text, record) => {
          // Если есть все необходимые ID для перехода к спецификации
          if (record.clientId && record.leadId && record.id) {
            return (
              <Typography.Link
                onClick={() =>
                  navigate(`/lead/${record.clientId}/${record.leadId}/${record.id}/edit`)
                }
                style={{ cursor: 'pointer' }}
              >
                {text || '-'}
              </Typography.Link>
            );
          }
          return text || '-';
        },
      },
      {
        title: 'Производство',
        dataIndex: 'productionCost',
        key: 'productionCost',
        width: 160,
        render: (value) => formatCurrency(value),
      },
      {
        title: 'Сборка',
        dataIndex: 'assemblyCost',
        key: 'assemblyCost',
        width: 140,
        render: (value) => formatCurrency(value),
      },
      {
        title: 'Монтаж',
        dataIndex: 'installationCost',
        key: 'installationCost',
        width: 140,
        render: (value) => formatCurrency(value),
      },
      {
        title: 'Фурнитура',
        dataIndex: 'furnitureCost',
        key: 'furnitureCost',
        width: 140,
        render: (value) => formatCurrency(value),
      },
      {
        title: 'Админ',
        dataIndex: 'adminCost',
        key: 'adminCost',
        width: 140,
        render: (value) => formatCurrency(value),
      },
      {
        title: 'Алюминий',
        dataIndex: 'aluminiumCost',
        key: 'aluminiumCost',
        width: 140,
        render: (value) => formatCurrency(value),
      },
      {
        title: 'Дизайнер',
        dataIndex: 'designerCost',
        key: 'designerCost',
        width: 140,
        render: (value) => formatCurrency(value),
      },
      {
        title: 'Аккаунт',
        dataIndex: 'accountCost',
        key: 'accountCost',
        width: 140,
        render: (value) => formatCurrency(value),
      },
      {
        title: 'ОП',
        dataIndex: 'operationalCost',
        key: 'operationalCost',
        width: 140,
        render: (value) => formatCurrency(value),
      },
      {
        title: 'Банк %',
        dataIndex: 'bankPercent',
        key: 'bankPercent',
        width: 140,
        render: (value) => formatCurrency(value),
      },
      {
        title: 'Итого себестоимость',
        dataIndex: 'totalCost',
        key: 'totalCost',
        width: 180,
        render: (value) => formatCurrency(value),
      },
      {
        title: 'Валовая прибыль',
        dataIndex: 'grossProfit',
        key: 'grossProfit',
        width: 180,
        render: (value) => formatCurrency(value),
      },
      {
        title: 'ROI',
        dataIndex: 'roi',
        key: 'roi',
        width: 100,
        render: (value) => formatPercent(value),
      },
    ],
    [formatCurrency, formatPercent, navigate]
  );

  const financeRows = useMemo(
    () =>
      (data?.financeTable || []).map((item) => ({
        key: item.id,
        ...item,
      })),
    [data?.financeTable]
  );

  return (
    <div>
      <Card style={{ marginBottom: 24 }}>
        <Space size="middle" direction="vertical" style={{ width: '100%' }}>
          <Space
            style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
          >
            <Text strong style={{ fontSize: 16 }}>
              Отдел: {getDepartmentName(departmentType)}
            </Text>
            <Button
              type="text"
              icon={<ReloadOutlined />}
              onClick={loadData}
              loading={loading}
            >
              Обновить
            </Button>
          </Space>
          <Space size="small" wrap>
            <RangePicker
              value={dateRange}
              onChange={(dates) => dates && setDateRange(dates)}
              format="DD.MM.YYYY"
              allowClear={false}
            />
            <Button onClick={() => setQuickRange('today')}>Сегодня</Button>
            <Button onClick={() => setQuickRange('week')}>Неделя</Button>
            <Button onClick={() => setQuickRange('month')}>Месяц</Button>
          </Space>
        </Space>
      </Card>

      <Spin spinning={loading}>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {summaryCards.map((card) => (
            <Col xs={24} sm={12} md={8} lg={6} xl={6} key={card.key}>
              <Card bordered>
                <Space align="start" size="large">
                  {card.icon}
                  <Statistic
                    title={card.title}
                    value={card.value}
                    formatter={card.formatter}
                    valueStyle={{ fontSize: 20 }}
                  />
                </Space>
              </Card>
            </Col>
          ))}
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} md={12}>
            <Card>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text type="secondary">В процессе</Text>
                <Statistic value={data?.summary?.inProgressCount ?? 0} />
                <Divider style={{ margin: '12px 0' }} />
                <Text type="secondary">Сумма в ожидании</Text>
                <Text strong style={{ fontSize: 18 }}>
                  {formatCurrency(data?.summary?.pendingAmount ?? 0)}
                </Text>
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text type="secondary">Завершено</Text>
                <Statistic value={data?.summary?.completedCount ?? 0} />
                <Divider style={{ margin: '12px 0' }} />
                <Text type="secondary">Сумма к выплате</Text>
                <Text strong style={{ fontSize: 18 }}>
                  {formatCurrency(data?.summary?.payoutAmount ?? 0)}
                </Text>
              </Space>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} md={12}>
            <Card title="Расходы">
              <List
                dataSource={data?.expenses || []}
                locale={{ emptyText: 'Нет данных' }}
                renderItem={(item) => (
                  <List.Item>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Text>{item.name}</Text>
                      <Text strong>{formatCurrency(item.amount)}</Text>
                    </Space>
                  </List.Item>
                )}
              />
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Доходы">
              <List
                dataSource={data?.incomes || []}
                locale={{ emptyText: 'Нет данных' }}
                renderItem={(item) => (
                  <List.Item>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Text>{item.name}</Text>
                      <Text strong>{formatCurrency(item.amount)}</Text>
                    </Space>
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>

        <Card title="Финансы по заказам">
          <Table
            dataSource={financeRows}
            columns={columns}
            size="small"
            bordered
            pagination={{
              current: data?.pagination?.page || page,
              pageSize: data?.pagination?.limit || pageSize,
              total: data?.pagination?.total || 0,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} записей`,
              pageSizeOptions: ['10', '25', '50', '100'],
              onChange: (newPage, newPageSize) => {
                setPage(newPage);
                if (newPageSize !== pageSize) {
                  setPageSize(newPageSize);
                  setPage(1); // Сброс на первую страницу при изменении размера
                }
              },
            }}
            scroll={{ x: 2400 }}
          />
        </Card>
      </Spin>
    </div>
  );
};

export default ProductionFinances;

